import React, {FC, useId, useState} from 'react'
import {useIntl} from 'react-intl'

import WizardConfirmDialog from './WizardConfirmDialog'

/**
 * What Institution Setup asks before a saved branch leaves the list: `remove` confirms the delete
 * (it takes the branch's classes, subjects and teacher allocations with it); `rename` is for the
 * school's first branch, which holds the General level and can't be deleted, so it can only be
 * renamed.
 */
export type BranchRemovalRequest = {name: string; mode: 'remove' | 'rename'}

interface Props {
  request: BranchRemovalRequest | null
  /** Every branch name in the list; a rename must not take another branch's name. */
  branches: string[]
  isDark: boolean
  onCancel: () => void
  onRemove: (name: string) => void
  onRename: (from: string, to: string) => void
}

/** Same rule as the branch input: letters, numbers, spaces and hyphens only. */
const BRANCH_NAME = /^[\p{L}\p{N}\s-]+$/u

const RenameField: FC<{
  name: string
  branches: string[]
  isDark: boolean
  onCancel: () => void
  onRename: (from: string, to: string) => void
}> = ({name, branches, isDark, onCancel, onRename}) => {
  const intl = useIntl()
  const inputId = useId()
  const [value, setValue] = useState(name)
  const next = value.trim()
  const error = !next
    ? null
    : !BRANCH_NAME.test(next)
      ? intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCH_NAME_INVALID'})
      : branches.some(
            (branch) =>
              branch.toLowerCase() === next.toLowerCase() &&
              branch.toLowerCase() !== name.toLowerCase()
          )
        ? intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCH_DUPLICATE_ERROR'})
        : null
  const disabled = !next || next === name || error !== null
  const submit = () => {
    if (!disabled) onRename(name, next)
  }

  return (
    <WizardConfirmDialog
      open
      tone='primary'
      isDark={isDark}
      title={intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCH_FIRST_TITLE'}, {branch: name})}
      body={intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCH_FIRST_BODY'}, {branch: name})}
      confirmLabel={intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCH_RENAME_CONFIRM'})}
      cancelLabel={intl.formatMessage({id: 'GENERAL.CANCEL'})}
      confirmDisabled={disabled}
      onCancel={onCancel}
      onConfirm={submit}
    >
      <div style={{marginBottom: '1.1rem'}}>
        <label
          htmlFor={inputId}
          style={{
            display: 'block',
            fontSize: '0.8rem',
            fontWeight: 600,
            marginBottom: '0.35rem',
            color: isDark ? '#ffffff' : '#181C32',
          }}
        >
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCH_RENAME_LABEL'})}
        </label>
        <input
          id={inputId}
          type='text'
          className='form-control'
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              submit()
            }
          }}
        />
        {error && (
          <p className='mt-2 mb-0' style={{color: '#f1416c', fontSize: '0.8rem'}}>
            {error}
          </p>
        )}
      </div>
    </WizardConfirmDialog>
  )
}

/** The confirm-remove / rename-instead dialog for a branch chip on Institution Setup. */
const BranchRemovalDialog: FC<Props> = ({
  request,
  branches,
  isDark,
  onCancel,
  onRemove,
  onRename,
}) => {
  const intl = useIntl()
  if (!request) return null
  if (request.mode === 'rename') {
    // Keyed by the branch so the field starts from its name each time it opens.
    return (
      <RenameField
        key={request.name}
        name={request.name}
        branches={branches}
        isDark={isDark}
        onCancel={onCancel}
        onRename={onRename}
      />
    )
  }
  return (
    <WizardConfirmDialog
      open
      tone='danger'
      isDark={isDark}
      title={intl.formatMessage(
        {id: 'ONBOARDING_WIZARD.STEP2.BRANCH_REMOVE_TITLE'},
        {branch: request.name}
      )}
      body={intl.formatMessage(
        {id: 'ONBOARDING_WIZARD.STEP2.BRANCH_REMOVE_BODY'},
        {branch: request.name}
      )}
      confirmLabel={intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCH_REMOVE_CONFIRM'})}
      cancelLabel={intl.formatMessage({id: 'GENERAL.CANCEL'})}
      onCancel={onCancel}
      onConfirm={() => onRemove(request.name)}
    />
  )
}

export default BranchRemovalDialog
